/**
 * Generate segmentation segments for collection iterators (loops)
 * Used in conditional display to allow conditions on parent loop variables
 */
// eslint-disable-next-line import/no-cycle
import {
  stringLogicalOperators,
  numberLogicalOperators,
  dateNullLogicalOperators,
  selectionBasedLogicalOperators,
} from '@/composables/segmentations/logicalOperators';
// eslint-disable-next-line import/no-cycle
import {
  CollectionContext,
  getCollectionSchema,
} from '@/composables/template-builder/collectionContext';

/**
 * Map custom data field types to logical operators
 */
const getLogicalOperatorsForType = (fieldType: string, fieldName: string) => {
  switch (fieldType) {
    case 'number':
    case 'int':
    case 'float':
    case 'decimal':
      return numberLogicalOperators(fieldName);
    case 'date':
    case 'datetime':
      return dateNullLogicalOperators(fieldName);
    case 'bool':
    case 'boolean':
      return selectionBasedLogicalOperators(fieldName, [
        { label: 'common.yes', value: true },
        { label: 'common.no', value: false },
      ]);
    case 'text':
    case 'string':
    default:
      return stringLogicalOperators(fieldName);
  }
};

/**
 * Native product properties for products collection
 * Must match properties defined in products.ts
 */
const NATIVE_PRODUCT_PROPERTIES = [
  { name: 'name', type: 'string', label: 'segmentations.products.attributes.product_name' },
  { name: 'price', type: 'number', label: 'segmentations.products.attributes.product_price' },
  { name: 'price_discount', type: 'number', label: 'segmentations.products.attributes.product_price_discount' },
  { name: 'quantity', type: 'number', label: 'segmentations.products.attributes.product_quantity' },
];

/**
 * Generate a segment group for a single collection context
 */
export const getCollectionIteratorSegments = async (
  collectionContext: CollectionContext,
): Promise<{ id: string; label: string; rawLabel: boolean; list: unknown[] } | null> => {
  const { collection, iterator, schema } = collectionContext;

  // Determine properties to use
  let properties: { name: string; type: string; label?: string }[] = [];

  if (schema && schema.properties) {
    // Use schema properties for custom collections
    properties = schema.properties.map((prop: any) => ({
      name: prop.name,
      type: prop.type || 'string',
      label: prop.name, // Use field name as label for custom fields
    }));
  } else if (collection === 'products') {
    // Use native product properties
    properties = NATIVE_PRODUCT_PROPERTIES;
  } else {
    // No properties available for this collection
    return null;
  }

  if (properties.length === 0) {
    return null;
  }

  // Generate segment list
  const segmentList = properties.map((prop) => {
    const fieldId = `${iterator}_${prop.name}`;
    const contextPath = `${iterator}.${prop.name}`;

    return {
      id: fieldId,
      label: prop.label || `${iterator}.${prop.name}`,
      rawLabel: !prop.label?.startsWith('segmentations.'), // Use raw label for custom fields
      logical_operators: getLogicalOperatorsForType(prop.type, prop.name),
      context: contextPath,
      isIteration: true, // Mark as iteration segment for badge display
    };
  });

  // Determine group label
  // For products, use translation key; for custom schemas, use raw label
  let groupLabel: string;
  let useRawLabel = true;
  if (schema && schema.schemaName) {
    groupLabel = `${schema.schemaName} (${iterator})`;
  } else if (collection === 'products') {
    groupLabel = 'segmentations.products.iterationLabel';
    useRawLabel = false;
  } else {
    groupLabel = `${collection} (${iterator})`;
  }

  return {
    id: `collection_${iterator}`,
    label: groupLabel,
    rawLabel: useRawLabel,
    list: segmentList,
  };
};

/**
 * Generate segment groups for all parent collection contexts
 */
export const getAllParentCollectionSegments = async (
  contexts: CollectionContext[],
): Promise<{ id: string; label: string; rawLabel: boolean; list: unknown[] }[]> => {
  const segmentGroups: { id: string; label: string; rawLabel: boolean; list: unknown[] }[] = [];

  // Process each context
  await Promise.all(
    contexts.map(async (context) => {
      const segmentGroup = await getCollectionIteratorSegments(context);
      if (segmentGroup && segmentGroup.list.length > 0) {
        segmentGroups.push(segmentGroup);
      }
    }),
  );

  return segmentGroups;
};

/**
 * Detect collection contexts from an HTML element and generate segments
 * This is a convenience function that combines detection and generation
 */
export const getCollectionSegmentsFromElement = async (
  element: HTMLElement | null,
): Promise<{ id: string; label: string; rawLabel: boolean; list: unknown[] }[]> => {
  if (!element) {
    return [];
  }

  const contexts: CollectionContext[] = [];
  let current: HTMLElement | null = element;

  // Walk up the DOM tree to find all parent collections
  while (current) {
    const collectionAttr = current.getAttribute('s-collection');
    const itemAttr = current.getAttribute('s-item');

    if (collectionAttr && itemAttr) {
      // Get schema for this collection
      const { schema } = await getCollectionSchema(collectionAttr);

      contexts.push({
        collection: collectionAttr,
        iterator: itemAttr,
        schema,
      });
    }

    current = current.parentElement;
  }

  return getAllParentCollectionSegments(contexts);
};

export default getCollectionIteratorSegments;
